import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store/store';
import {
	Box,
	BoxOverflow,
	Button,
	IconButton,
	Stack,
	Typography,
} from 'styles';
import palette from 'styles/theme/Palette';
import { ISettlementLettersDocument } from 'types/SettlementLetters';
import { ReactComponent as ChevronLeft } from '../../../assett/icons/chevron_left_icon.svg';
import { ReactComponent as ChevronRight } from '../../../assett/icons/chevron_right_icon.svg';
import { ReactComponent as DownloadIcon } from '../../../assett/icons/download.svg';

import FileRenderer from 'components/commons/FileRenderer/FileRenderer';
import { toggleExpandView } from 'feature/settlementLetters/settlementLettersSlice';
import useDialogConfirm from 'hooks/DialogConfirm.hook';
import { useFileOpener } from 'hooks/downloader.hook';
import { GENERIC_ERROR } from 'types/dialogTypes';
import { MIME_TYPES } from 'types/Documents';
import { TRANSLATION_KEYS } from 'utils/constants';
import { useTranslation } from 'utils/translations';

type Props = {
	selectedDocument: ISettlementLettersDocument | undefined;
};

// FIXME: Assuming PDF is the only type handled here, correct MIME type should come from BE list
const MIME = MIME_TYPES.PDF[0];

const DocumentRenderer = ({ selectedDocument }: Props) => {
	const dispatch = useDispatch<AppDispatch>();
	const { t: tClientLabels } = useTranslation('', {
		keyPrefix: TRANSLATION_KEYS.CLIENT_LABELS,
	});
	const fileOpener = useFileOpener();
	const dialogConfirm = useDialogConfirm();

	const { expandView, document } = useSelector(
		(store: RootState) => store.settlementLetters,
	);

	const downloadDoc = () => {
		if (selectedDocument?.uuid && document) {
			fileOpener({
				url: `/carrier-docs/${selectedDocument.uuid}/content`,
				fileName: `${selectedDocument?.name}.${MIME.split('/')[1]}`,
				allowDownload: true,
			});
			return;
		}

		dialogConfirm.open(GENERIC_ERROR);
	};

	const handleClickToggleList = () => {
		dispatch(toggleExpandView());
	};

	return (
		<Stack
			borderRadius={'4px'}
			bgcolor={palette.grey5.main}
			height={'calc(100dvh - 330px)'}
		>
			<Box
				sx={{
					borderTopRightRadius: '4px',
					borderTopLeftRadius: '4px',
				}}
				height={'52px'}
				display={'flex'}
				flexDirection={'row'}
				justifyContent={'space-between'}
				alignItems={'center'}
				width={'100%'}
				py={'10px'}
				px={'20px'}
				bgcolor={palette.grey4.main}
			>
				<Button
					variant="text"
					sx={{
						paddingX: 0,
						paddingY: '10px',
						gap: `${expandView ? '15px' : '5px'}`,
						textTransform: 'capitalize',
					}}
					startIcon={expandView ? <ChevronLeft /> : null}
					endIcon={!expandView ? <ChevronRight /> : null}
					onClick={handleClickToggleList}
				>
					{expandView ? (
						<>{tClientLabels('commons.expand')}</>
					) : (
						<>{tClientLabels('commons.collapse')}</>
					)}
				</Button>
				<Typography variant="bodyLink">{selectedDocument?.name}</Typography>
				<Box
					display={'flex'}
					flexDirection={'row'}
					justifyContent={'flex-end'}
					sx={{
						width: {
							sm: '80px',
							lg: '84px',
						},
					}}
				>
					{selectedDocument && (
						<IconButton
							disableRipple
							disableTouchRipple
							onClick={downloadDoc}
							aria-label={`${tClientLabels('commons.buttons.download')}`}
						>
							<DownloadIcon width={24} height={24} color={palette.primary.main} />
						</IconButton>
					)}
				</Box>
			</Box>
			<BoxOverflow
				variant="y"
				sx={{ marginX: '20px', marginY: '15px', paddingRight: '8px' }}
			>
				<Box display={'flex'} justifyContent={'center'}>
					<Box mx={'auto'}>
						{selectedDocument && document && (
							<FileRenderer mime={MIME} url={document} name={selectedDocument.name} />
						)}
					</Box>
				</Box>
			</BoxOverflow>
		</Stack>
	);
};

export default DocumentRenderer;
